import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Grid,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PeopleIcon from '@mui/icons-material/People';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportAPI } from '../../services/reportAPI';
import { formatDateForInput } from '../../utils/dateUtils';

// ---- Types ----------------------------------------------------------------

interface MemberActivityBreakdown {
    classBookings: number;
    classMinutes: number;
    amenityBookings: number;
    amenityMinutes: number;
    coachingBookings: number;
    coachingMinutes: number;
    groupPlayBookings: number;
    groupPlayMinutes: number;
    guestPlayBookings: number;
    guestPlayMinutes: number;
}

interface MemberUtilizationRow {
    memberId: string;
    memberName: string;
    membershipTier: string | null;
    visits: number;
    totalBookings: number;
    totalMinutes: number;
    breakdown: MemberActivityBreakdown;
}

interface MemberUtilizationData {
    members: MemberUtilizationRow[];
    amenityTypes: string[];
}

// ---- Helpers --------------------------------------------------------------

const fmtMinH = (minutes: number) =>
    `${(minutes / 60).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`;

const RANK_BY_OPTIONS = [
    { value: 'visits',   label: 'Visits' },
    { value: 'bookings', label: 'Bookings' },
    { value: 'hours',    label: 'Hours' },
] as const;

const ACTIVITY_SOURCE_OPTIONS = [
    { value: 'all',        label: 'All activities' },
    { value: 'classes',    label: 'Classes' },
    { value: 'amenity',    label: 'Court / Amenity bookings' },
    { value: 'coaching',   label: 'Coaching sessions' },
    { value: 'group_play', label: 'Group play' },
    { value: 'guest_play', label: 'Guest play' },
] as const;

const TOP_N_OPTIONS = [10, 15, 25, 50, 0] as const;

// ---- Component ------------------------------------------------------------

export const MemberActivityReport: React.FC = () => {
    const now = new Date();

    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState<string | null>(null);
    const [reportData, setReportData] = useState<MemberUtilizationData | null>(null);

    // Filters
    const [startDate, setStartDate]           = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate]               = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const [rankBy, setRankBy]                 = useState<'visits' | 'bookings' | 'hours'>('visits');
    const [activitySource, setActivitySource] = useState<'all' | 'classes' | 'amenity' | 'coaching' | 'group_play' | 'guest_play'>('all');
    const [amenityType, setAmenityType]       = useState<string>('all');
    const [topN, setTopN]                     = useState<number>(15);
    const [tierFilter, setTierFilter]         = useState<string>('all');

    useEffect(() => {
        loadReport();
    }, [startDate, endDate, rankBy, activitySource, amenityType, topN]);

    const loadReport = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await reportAPI.getMemberUtilizationReport({
                startDate:      formatDateForInput(startDate),
                endDate:        formatDateForInput(endDate),
                rankBy,
                activitySource,
                amenityType:    activitySource === 'amenity' && amenityType !== 'all' ? amenityType : undefined,
                topN,
            });
            setReportData(data);
        } catch (err) {
            console.error('Error loading member activity report:', err);
            setError('Failed to load member activity report');
        } finally {
            setLoading(false);
        }
    };

    // Primary rank column value and label
    const rankLabel = RANK_BY_OPTIONS.find(o => o.value === rankBy)?.label ?? '';

    const rankValue = (row: MemberUtilizationRow): string => {
        if (rankBy === 'visits')   return String(row.visits);
        if (rankBy === 'bookings') return String(row.totalBookings);
        return fmtMinH(row.totalMinutes);
    };

    // Derive available tiers and apply tier filter client-side
    const availableTiers = reportData
        ? Array.from(new Set(reportData.members.map(m => m.membershipTier).filter(Boolean))).sort() as string[]
        : [];

    const displayedMembers = reportData
        ? (tierFilter === 'all' ? reportData.members : reportData.members.filter(m => m.membershipTier === tierFilter))
        : [];

    // Whether breakdown columns are relevant for the selected source
    const showClasses    = activitySource === 'all' || activitySource === 'classes';
    const showAmenity    = activitySource === 'all' || activitySource === 'amenity';
    const showCoaching   = activitySource === 'all' || activitySource === 'coaching';
    const showGroupPlay  = activitySource === 'all' || activitySource === 'group_play';
    const showGuestPlay  = activitySource === 'all' || activitySource === 'guest_play';

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Member Activity</Typography>
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box>
                    {/* Header + filters */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
                        <Typography variant="h4">Member Activity Report</Typography>

                        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={v => v && setStartDate(v)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={v => v && setEndDate(v)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                            <FormControl size="small" sx={{ minWidth: 175 }}>
                                <InputLabel>Rank By</InputLabel>
                                <Select
                                    label="Rank By"
                                    value={rankBy}
                                    onChange={e => setRankBy(e.target.value as any)}
                                >
                                    {RANK_BY_OPTIONS.map(o => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Activity</InputLabel>
                                <Select
                                    label="Activity"
                                    value={activitySource}
                                    onChange={e => {
                                        setActivitySource(e.target.value as any);
                                        setAmenityType('all');
                                    }}
                                >
                                    {ACTIVITY_SOURCE_OPTIONS.map(o => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {activitySource === 'amenity' && (
                                <FormControl size="small" sx={{ minWidth: 170 }}>
                                    <InputLabel>Amenity Type</InputLabel>
                                    <Select
                                        label="Amenity Type"
                                        value={amenityType}
                                        onChange={e => setAmenityType(e.target.value)}
                                    >
                                        <MenuItem value="all">All Types</MenuItem>
                                        {(reportData?.amenityTypes ?? []).map(t => (
                                            <MenuItem key={t} value={t}>{t}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel>Tier</InputLabel>
                                <Select
                                    label="Tier"
                                    value={tierFilter}
                                    onChange={e => setTierFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Tiers</MenuItem>
                                    {availableTiers.map(t => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                <InputLabel>Top #</InputLabel>
                                <Select
                                    label="Top N"
                                    value={topN}
                                    onChange={e => setTopN(Number(e.target.value))}
                                >
                                    {TOP_N_OPTIONS.map(n => (
                                        <MenuItem key={n} value={n}>{n === 0 ? 'All' : `Top ${n}`}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Loading / error */}
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {!loading && reportData && (
                        <>
                            {/* Summary card */}
                           {/* <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Card elevation={2}>
                                        <CardContent>
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                <Typography color="textSecondary" variant="body2">
                                                    Active Members Shown
                                                </Typography>
                                            </Box>
                                            <Typography variant="h4" fontWeight="bold">
                                                {reportData.members.length}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Ranked by {rankLabel.toLowerCase()}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {rankBy === 'visits' && (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Card elevation={2}>
                                            <CardContent>
                                                <Typography color="textSecondary" variant="body2" gutterBottom>
                                                    Total Visits (shown members)
                                                </Typography>
                                                <Typography variant="h4" fontWeight="bold">
                                                    {reportData.members.reduce((s, m) => s + m.visits, 0).toLocaleString()}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {(rankBy === 'bookings' || rankBy === 'visits') && (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Card elevation={2}>
                                            <CardContent>
                                                <Typography color="textSecondary" variant="body2" gutterBottom>
                                                    Total Bookings (shown members)
                                                </Typography>
                                                <Typography variant="h4" fontWeight="bold">
                                                    {reportData.members.reduce((s, m) => s + m.totalBookings, 0).toLocaleString()}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {(rankBy === 'hours' || rankBy === 'bookings') && (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Card elevation={2}>
                                            <CardContent>
                                                <Typography color="textSecondary" variant="body2" gutterBottom>
                                                    Total Hours (shown members)
                                                </Typography>
                                                <Typography variant="h4" fontWeight="bold">
                                                    {fmtMinH(reportData.members.reduce((s, m) => s + m.totalMinutes, 0))}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid> */}

                            {/* Table */}
                            <Paper elevation={2} sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    {topN === 0 ? 'All' : `Top ${topN}`} Members — {rankLabel}
                                    {tierFilter !== 'all' && ` · ${tierFilter}`}
                                </Typography>

                                {displayedMembers.length === 0 ? (
                                    <Typography color="textSecondary">
                                        No member activity found for the selected filters.
                                    </Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>#</strong></TableCell>
                                                    <TableCell><strong>Member</strong></TableCell>
                                                    <TableCell><strong>Tier</strong></TableCell>
                                                    {rankBy !== 'visits' || activitySource === 'all' ? (
                                                        <TableCell align="right"><strong>Visits</strong></TableCell>
                                                    ) : null}
                                                    <TableCell align="right"><strong>{rankLabel}</strong></TableCell>
                                                    {showClasses && (
                                                        <TableCell align="right"><strong>Classes</strong></TableCell>
                                                    )}
                                                    {showAmenity && (
                                                        <TableCell align="right"><strong>Court / Amenity</strong></TableCell>
                                                    )}
                                                    {showCoaching && (
                                                        <TableCell align="right"><strong>Coaching</strong></TableCell>
                                                    )}
                                                    {showGroupPlay && (
                                                        <TableCell align="right"><strong>Group Play</strong></TableCell>
                                                    )}
                                                    {showGuestPlay && (
                                                        <TableCell align="right"><strong>Guest Play</strong></TableCell>
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {displayedMembers.map((member, idx) => (
                                                    <TableRow key={member.memberId} hover>
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={idx < 3 ? 'bold' : 'normal'}
                                                                color={idx === 0 ? 'warning.main' : 'text.primary'}
                                                            >
                                                                {idx + 1}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={idx < 3 ? 'medium' : 'normal'}>
                                                                {member.memberName}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {member.membershipTier ? (
                                                                <Chip label={member.membershipTier} size="small" variant="outlined" />
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                                            )}
                                                        </TableCell>
                                                        {(rankBy !== 'visits' || activitySource === 'all') && (
                                                            <TableCell align="right">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {member.visits}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        <TableCell align="right">
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {rankValue(member)}
                                                            </Typography>
                                                        </TableCell>
                                                        {showClasses && (
                                                            <TableCell align="right">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {member.breakdown.classBookings > 0
                                                                        ? `${member.breakdown.classBookings} (${fmtMinH(member.breakdown.classMinutes)})`
                                                                        : '—'}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        {showAmenity && (
                                                            <TableCell align="right">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {member.breakdown.amenityBookings > 0
                                                                        ? `${member.breakdown.amenityBookings} (${fmtMinH(member.breakdown.amenityMinutes)})`
                                                                        : '—'}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        {showCoaching && (
                                                            <TableCell align="right">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {member.breakdown.coachingBookings > 0
                                                                        ? `${member.breakdown.coachingBookings} (${fmtMinH(member.breakdown.coachingMinutes)})`
                                                                        : '—'}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        {showGroupPlay && (
                                                            <TableCell align="right">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {member.breakdown.groupPlayBookings > 0
                                                                        ? `${member.breakdown.groupPlayBookings} (${fmtMinH(member.breakdown.groupPlayMinutes)})`
                                                                        : '—'}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        {showGuestPlay && (
                                                            <TableCell align="right">
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {member.breakdown.guestPlayBookings > 0
                                                                        ? `${member.breakdown.guestPlayBookings} (${fmtMinH(member.breakdown.guestPlayMinutes)})`
                                                                        : '—'}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </>
                    )}

                    {!loading && !error && !reportData && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                            <Typography color="textSecondary">Select a date range to load the report.</Typography>
                        </Box>
                    )}
                </Box>
            </LocalizationProvider>
        </Container>
    );
};

export default MemberActivityReport;
